import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FileText, PenTool, Edit2 } from 'lucide-react';
import Header from '../components/Layout/Header';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 100px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 20px;
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const FormDescription = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const CompletedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${({ theme }) => `${theme.colors.success}20`};
  color: ${({ theme }) => theme.colors.successText};
  border-radius: 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin-bottom: 20px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin-bottom: 24px;
`;

const FormField = styled.div`
  margin-bottom: 24px;
`;

const FieldLabel = styled.div`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FieldValue = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text};
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  min-height: ${({ $multiline }) => $multiline ? '80px' : 'auto'};
  white-space: pre-wrap;
`;

const SelectedOption = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${({ theme }) => `${theme.colors.primary}10`};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};

  &::before {
    content: '✓';
    font-weight: bold;
  }
`;

const SignatureBox = styled.div`
  width: 100%;
  min-height: 120px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${({ theme }) => `${theme.colors.primary}05`};
  color: ${({ theme }) => theme.colors.primary};
  padding: 12px;
  overflow: hidden;

  svg {
    width: 32px;
    height: 32px;
  }

  span {
    font-size: 14px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }
`;

const SignatureImage = styled.img`
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
`;

const EditButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 12px;
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
  }
`;

const NotFoundMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.text};
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

// Formularios disponibles con sus campos
const availableForms = [
  {
    id: 1,
    name: 'Consentimiento Informado',
    description: 'Formulario de consentimiento para procedimientos médicos',
    fields: [
      { id: 1, type: 'text', label: 'Nombre completo del paciente', required: true },
      { id: 2, type: 'text', label: 'Número de identificación', required: true },
      { id: 3, type: 'textarea', label: 'Descripción del procedimiento', required: true },
      { id: 4, type: 'multiple', label: '¿Ha sido informado de los riesgos?', options: ['Sí', 'No'], required: true },
      { id: 5, type: 'signature', label: 'Firma del paciente', required: true },
    ],
  },
  {
    id: 2,
    name: 'Historia Clínica Inicial',
    description: 'Formulario para recopilar información médica del paciente',
    fields: [
      { id: 1, type: 'text', label: 'Nombre completo', required: true },
      { id: 2, type: 'text', label: 'Fecha de nacimiento', required: true },
      { id: 3, type: 'textarea', label: 'Antecedentes médicos', required: false },
      { id: 4, type: 'textarea', label: 'Alergias conocidas', required: true },
      { id: 5, type: 'multiple', label: '¿Toma medicamentos actualmente?', options: ['Sí', 'No'], required: true },
      { id: 6, type: 'textarea', label: 'Lista de medicamentos', required: false },
    ],
  },
  {
    id: 3,
    name: 'Encuesta de Satisfacción',
    description: 'Formulario para evaluar la experiencia del paciente',
    fields: [
      { id: 1, type: 'multiple', label: '¿Cómo calificaría la atención recibida?', options: ['Excelente', 'Buena', 'Regular', 'Mala'], required: true },
      { id: 2, type: 'multiple', label: '¿Recomendaría nuestros servicios?', options: ['Definitivamente sí', 'Probablemente sí', 'Probablemente no', 'Definitivamente no'], required: true },
      { id: 3, type: 'textarea', label: 'Comentarios adicionales', required: false },
    ],
  }
];

// Datos de ejemplo de formularios completados por pacientes
const completedFormsData = {
  // Paciente 1, Formulario 1
  '1-1': {
    1: 'María García López',
    2: 'INE-12345678',
    3: 'Extracción de muela del juicio inferior derecha bajo anestesia local.',
    4: 'Sí',
    5: 'signed'
  },
  // Paciente 1, Formulario 2
  '1-2': {
    1: 'María García López',
    2: '15/03/1985',
    3: 'Hipertensión controlada desde 2018. Cirugía de apendicitis en 2010.',
    4: 'Penicilina - reacción alérgica moderada',
    5: 'Sí',
    6: 'Losartán 50mg - 1 vez al día'
  }
};

const VerFormularioCompletado = () => {
  const { formId, patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const form = availableForms.find(f => f.id === parseInt(formId));
  const formValues = completedFormsData[`${patientId}-${formId}`] || {};
  const returnTo = location.state?.returnTo || `/perfil-paciente/${patientId}`;
  const completedAt = location.state?.completedAt || new Date().toISOString().split('T')[0];

  const renderFieldValue = (field) => {
    const value = formValues[field.id];
    
    if (!value) {
      return <FieldValue style={{ color: '#999', fontStyle: 'italic' }}>Sin respuesta</FieldValue>;
    }

    switch (field.type) {
      case 'text':
        return <FieldValue>{value}</FieldValue>;
      case 'textarea':
        return <FieldValue $multiline>{value}</FieldValue>;
      case 'multiple':
        return <SelectedOption>{value}</SelectedOption>;
      case 'signature':
        // Verificar si es una firma real (data URL) o solo el marcador 'signed'
        const isRealSignature = value && value.startsWith('data:');
        return (
          <SignatureBox>
            {isRealSignature ? (
              <>
                <SignatureImage src={value} alt="Firma del paciente" />
                <span>Firmado ✓</span>
              </>
            ) : (
              <>
                <PenTool />
                <span>Firmado ✓</span>
              </>
            )}
          </SignatureBox>
        );
      default:
        return <FieldValue>{value}</FieldValue>;
    }
  };

  const handleEdit = () => {
    navigate(`/llenar-formulario/${formId}/${patientId}`, {
      state: { 
        returnTo,
        editMode: true,
        existingValues: formValues
      }
    });
  };

  if (!form) {
    return (
      <PageContainer>
        <Header title="Formulario Completado" showBack />
        <Content>
          <NotFoundMessage>
            <FileText />
            <h3>Formulario no encontrado</h3>
            <p>El formulario que buscas no existe</p>
          </NotFoundMessage>
        </Content>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header title="Formulario Completado" showBack />
      
      <Content>
        <FormCard>
          <FormTitle>{form.name}</FormTitle>
          <FormDescription>{form.description}</FormDescription>
          
          <CompletedBadge>
            ✓ Completado el {completedAt}
          </CompletedBadge>
          
          <Divider />
          
          {form.fields.map((field) => (
            <FormField key={field.id}>
              <FieldLabel>{field.label}</FieldLabel>
              {renderFieldValue(field)}
            </FormField>
          ))}

          <EditButton onClick={handleEdit}>
            <Edit2 size={20} />
            Editar Formulario
          </EditButton>
        </FormCard>
      </Content>
    </PageContainer>
  );
};

export default VerFormularioCompletado;
